import React from "react";

/**
 * Animated "system of proof" hero visual.
 * Sits OVER the always-dark brand gradient, so it stays white + lilac (#C9B8FB /
 * #A179FA on-dark accent). Reads as evidence being generated live: roadmap
 * pathways draw in, nodes light up in sequence with a travelling "proof" pulse,
 * a scan sweep passes over, and data marks grow. Honours prefers-reduced-motion
 * (see .ek-hero-* keyframes in index.css).
 */
const NODES = [
  { cx: 90, cy: 120, r: 5, key: true }, { cx: 230, cy: 90, r: 4 },
  { cx: 370, cy: 145, r: 6, key: true }, { cx: 510, cy: 175, r: 4 },
  { cx: 150, cy: 230, r: 4 }, { cx: 320, cy: 240, r: 5, key: true },
  { cx: 460, cy: 280, r: 4 }, { cx: 120, cy: 380, r: 5 },
  { cx: 280, cy: 360, r: 4 }, { cx: 440, cy: 340, r: 6, key: true },
];
const LILAC = "#C9B8FB";

const HeroVisual = () => (
  <div className="relative w-full aspect-[5/4] md:aspect-[6/5] overflow-hidden" data-testid="hero-visual">
    <svg viewBox="0 0 600 500" preserveAspectRatio="xMidYMid slice"
         className="absolute inset-0 w-full h-full" aria-hidden="true">
      <defs>
        <linearGradient id="lineFade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="50%" stopColor="#ffffff" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="lineFade2" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="50%" stopColor={LILAC} stopOpacity="0.5" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="scanFade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={LILAC} stopOpacity="0" />
          <stop offset="50%" stopColor={LILAC} stopOpacity="0.55" />
          <stop offset="100%" stopColor={LILAC} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* subtle grid */}
      <g stroke="#ffffff" strokeOpacity="0.07" strokeWidth="1">
        {Array.from({ length: 8 }).map((_, i) => (
          <line key={"h" + i} x1="0" y1={i * 64} x2="600" y2={i * 64} />
        ))}
        {Array.from({ length: 10 }).map((_, i) => (
          <line key={"v" + i} x1={i * 64} y1="0" x2={i * 64} y2="500" />
        ))}
      </g>

      {/* Roadmap pathways — draw themselves in */}
      <g fill="none">
        <path d="M-20 120 Q 200 70 420 150 T 640 200" stroke="url(#lineFade)" strokeWidth="1.6"
              pathLength="1" className="ek-hero-draw" style={{ animationDelay: "0.1s" }} />
        <path d="M-20 220 Q 220 200 360 240 T 640 290" stroke="url(#lineFade2)" strokeWidth="1.4"
              pathLength="1" className="ek-hero-draw" style={{ animationDelay: "0.5s" }} />
        <path d="M-20 360 Q 200 380 380 320 T 640 380" stroke="url(#lineFade)" strokeWidth="1.6"
              pathLength="1" className="ek-hero-draw" style={{ animationDelay: "0.9s" }} />
      </g>

      {/* Travelling "proof" pulse along the top roadmap */}
      <circle r="3.5" fill={LILAC} className="ek-hero-comet">
        <animateMotion dur="4.5s" repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1"
                       calcMode="spline" keySplines="0.4 0 0.2 1"
                       path="M-20 120 Q 200 70 420 150 T 640 200" />
      </circle>

      {/* Vertical accent lines */}
      <g opacity="0.4">
        <line x1="90" y1="60" x2="90" y2="460" stroke="#ffffff" strokeWidth="1" strokeDasharray="2 6" />
        <line x1="510" y1="60" x2="510" y2="460" stroke="#ffffff" strokeWidth="1" strokeDasharray="2 6" />
      </g>

      {/* Scan sweep */}
      <rect x="-60" y="40" width="60" height="420" fill="url(#scanFade)" className="ek-hero-scan" />

      {/* Nodes — light up in sequence; key nodes glow lilac */}
      <g>
        {NODES.map((n, i) => (
          <g key={i} className="ek-hero-node" style={{ animationDelay: `${0.6 + i * 0.13}s` }}>
            <circle cx={n.cx} cy={n.cy} r={n.r + 6}
                    fill={n.key ? LILAC : "#ffffff"} opacity={n.key ? 0.22 : 0.16}
                    className={n.key ? "ek-hero-halo" : ""}
                    style={{ transformBox: "fill-box", transformOrigin: "center", animationDelay: `${1 + i * 0.13}s` }} />
            <circle cx={n.cx} cy={n.cy} r={n.r} fill={n.key ? LILAC : "#ffffff"} />
          </g>
        ))}
      </g>

      {/* Data marks — grow from baseline */}
      <g>
        {[14, 20, 26, 32, 38, 32, 26, 20, 14].map((h, i) => (
          <rect key={i} x={40 + i * 12} y={444 - h} width="6" height={h}
                fill="#ffffff" opacity={0.4 + i * 0.05}
                className="ek-hero-bar"
                style={{ transformBox: "fill-box", transformOrigin: "bottom", animationDelay: `${1.2 + i * 0.08}s` }} />
        ))}
      </g>

      {/* Labels */}
      <g fontFamily="Work Sans, sans-serif" fontSize="9" fill="#ffffff" opacity="0.55" letterSpacing="2">
        <text x="20" y="36">DISCOVER · DIAGNOSE</text>
        <text x="20" y="50" opacity="0.5">ROADMAP 01</text>
        <text x="445" y="36">DESIGN · DELIVER</text>
        <text x="445" y="50" opacity="0.5">ROADMAP 02</text>
        <text x="20" y="480">EMBED · OPTIMISE</text>
      </g>
    </svg>

    {/* Frame */}
    <div className="absolute inset-0 border border-white/15 pointer-events-none" />
  </div>
);

export default HeroVisual;
